import { z } from 'zod'
import { oc } from '@orpc/contract'
import {
  AI_STATUS_VALUES,
  DISCOVERY_KINDS,
  LAB_VENDORS,
  NPPES_STATUS_VALUES,
  RADIOLOGY_DISCOVERY_MODALITIES,
} from '../facility-discovery-shared.schema'

export const nppesViewSchema = z.object({
  status: z.enum(NPPES_STATUS_VALUES),
  npi: z.string().optional(),
  taxonomy: z.string().optional(),
  legalName: z.string().optional(),
})
export const aiViewSchema = z.object({
  status: z.enum(AI_STATUS_VALUES),
  procedures: z.array(z.string()),
  insurances: z.array(z.string()),
  sourceUrls: z.array(z.string()),
})
export const enrichmentViewSchema = z.object({
  nppes: nppesViewSchema,
  ai: aiViewSchema,
  fax: z.string().optional(),
  faxSource: z.enum(['saved', 'nppes', 'website']).optional(),
  orderCount: z.number().int(),
})

export const discoveredFacilitySchema = enrichmentViewSchema.extend({
  placeId: z.string(),
  name: z.string(),
  street: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  // Absent for a saved facility without a geolocation extension in the coordinate-free fallback.
  lat: z.number().optional(),
  lon: z.number().optional(),
  // Absent when the search ran without patient coordinates (see input schema).
  distanceMiles: z.number().optional(),
  rating: z.number().optional(),
  ratingCount: z.number().optional(),
  openNow: z.boolean().optional(),
  hours: z.array(z.string()).optional(),
  phone: z.string().optional(),
  website: z.string().optional(),
  mapsUrl: z.string(),
  businessStatus: z.enum(['OPERATIONAL', 'CLOSED_TEMPORARILY', 'CLOSED_PERMANENTLY', 'UNKNOWN']),
  organizationId: z.string().optional(),
})

// Query-string transport: numbers arrive as strings, hence z.coerce.
// Exported (not inlined into .input()) so the contract spec imports a plain
// zod schema instead of reaching into the builder — see
// facility-discovery-search.contract.spec.ts for why.
// lat/lon are optional as a pair: when the patient's ZIP could not be geocoded the
// UI still asks for the saved in-state facilities (a coordinate-free legacy search),
// instead of showing nothing — the old picker never depended on a geocoder.
export const facilityDiscoverySearchInputSchema = z
  .object({
    lat: z.coerce.number().min(-90).max(90).optional(),
    lon: z.coerce.number().min(-180).max(180).optional(),
    radiusMiles: z.coerce
      .number()
      .pipe(z.union([z.literal(25), z.literal(50)]))
      .default(25),
    kind: z.enum(DISCOVERY_KINDS).default('radiology'),
    // Which lab chain to search. Ignored for radiology; the modality is ignored for labs.
    vendor: z.enum(LAB_VENDORS).optional(),
    modality: z.enum(RADIOLOGY_DISCOVERY_MODALITIES).optional(),
    query: z.string().trim().max(120).optional(),
    state: z.string().length(2).toUpperCase().optional(),
    // 'state' searches the patient's whole state by facility name instead of a radius.
    scope: z.enum(['radius', 'state']).default('radius'),
  })
  .refine((v) => (v.lat === undefined) === (v.lon === undefined), {
    message: 'lat and lon must be given together',
  })
  // Without a vendor a lab search has no brand phrase, no brand filter and no
  // parent to scope the seeded overlay to — it would return every chain's PSCs.
  .refine((v) => v.kind !== 'lab' || Boolean(v.vendor), {
    message: 'vendor is required when kind is lab',
  })
  // Without coordinates the state is the only bound on the legacy query; letting it
  // go too would return every saved facility in the country.
  .refine((v) => v.lat !== undefined || Boolean(v.state), {
    message: 'state is required when lat/lon are omitted',
  })
  .refine((v) => v.scope !== 'state' || Boolean(v.state), {
    message: 'state is required for a state-wide search',
    path: ['state'],
  })
  .refine((v) => v.scope !== 'state' || (v.query?.length ?? 0) >= 2, {
    message: 'a facility name of at least 2 characters is required for a state-wide search',
    path: ['query'],
  })
  // A lab search is pinned to a vendor's nearby PSCs; there is no state-wide lab picker.
  .refine((v) => v.scope !== 'state' || v.kind !== 'lab', {
    message: 'a state-wide search is not available for labs',
    path: ['scope'],
  })

export const facilityDiscoverySearch = oc
  .route({ method: 'GET', path: '/v2/facility-discovery/search' })
  .input(facilityDiscoverySearchInputSchema)
  .output(
    z.object({
      facilities: z.array(discoveredFacilitySchema),
      source: z.enum(['google', 'legacy']),
    }),
  )
